import React, { useState, useRef, useEffect } from 'react';
import { Shield, Eye, Flag, Radio, MessageSquare, Ban, CheckCircle2, ArrowRight, FileText, Upload, Camera } from 'lucide-react';
import { API_BASE as API } from '../../lib/apiBase';


const STEPS = [
  {
    icon: Shield,
    title_es: 'Bienvenido al equipo de moderacion',
    title_en: 'Welcome to the moderation team',
    desc_es: 'Como moderador de PulseMusic, tu rol es mantener la plataforma segura, limpia y respetuosa para todos los usuarios y artistas. Tienes acceso a herramientas de moderacion pero NO a datos financieros, configuracion del sistema ni informacion confidencial del negocio.',
    desc_en: 'As a PulseMusic moderator, your role is to keep the platform safe, clean and respectful for all users and artists. You have access to moderation tools but NOT to financial data, system settings or confidential business information.',
  },
  {
    icon: Eye,
    title_es: 'Revision de contenido',
    title_en: 'Content review',
    desc_es: 'Puedes revisar canciones en cola de verificacion y aprobar o rechazar contenido. Verifica que la musica subida cumple con las normas de la comunidad. Si tienes dudas sobre un contenido, marcalo para revision del equipo directivo.',
    desc_en: 'You can review songs in the verification queue and approve or reject content. Verify that uploaded music complies with community guidelines. If in doubt about content, flag it for the leadership team.',
  },
  {
    icon: Flag,
    title_es: 'Reportes de usuarios',
    title_en: 'User reports',
    desc_es: 'Los usuarios pueden reportar contenido inapropiado. Tu trabajo es revisar estos reportes, tomar accion (advertencia, retirada de contenido, suspension temporal) y documentar tu decision. Todas tus acciones quedan registradas en el log de auditoria.',
    desc_en: 'Users can report inappropriate content. Your job is to review these reports, take action (warning, content removal, temporary suspension) and document your decision. All your actions are logged in the audit trail.',
  },
  {
    icon: Radio,
    title_es: 'Moderacion en vivo',
    title_en: 'Live moderation',
    desc_es: 'Durante emisiones en vivo puedes: silenciar chat, banear usuarios del chat, limpiar mensajes inapropiados y, en casos extremos, terminar una emision que viole los terminos. Usa esta capacidad con responsabilidad.',
    desc_en: 'During live streams you can: mute chat, ban users from chat, clear inappropriate messages and, in extreme cases, terminate a stream that violates terms. Use this power responsibly.',
  },
  {
    icon: Ban,
    title_es: 'Lo que NO puedes hacer',
    title_en: 'What you CANNOT do',
    desc_es: 'No puedes: ver datos financieros, modificar configuracion del sistema, acceder a wallets de usuarios, ver acuerdos internos, cambiar roles de otros usuarios, ni acceder a informacion confidencial del negocio. Si necesitas algo fuera de tu alcance, contacta al equipo directivo.',
    desc_en: 'You cannot: view financial data, modify system settings, access user wallets, view internal agreements, change other users roles, or access confidential business information. If you need something outside your scope, contact the leadership team.',
  },
];

export default function ModeratorOnboarding({ token, lang, userName, onComplete, isModerationLead = false }) {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({ full_name: '', country: '', id_number: '', email: '', phone: '' });
  const [idPhoto, setIdPhoto] = useState(null);
  const [idPhotoPreview, setIdPhotoPreview] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [agreedData, setAgreedData] = useState(false);
  const [agreedConsequences, setAgreedConsequences] = useState(false);
  const [signing, setSigning] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const legalScrollRef = useRef(null);

  const isLastStep = step === STEPS.length;
  const currentStep = STEPS[step];

  const allFieldsComplete = formData.full_name.trim() && formData.country.trim() &&
    formData.id_number.trim() && formData.email.trim() && idPhoto && signatureData &&
    agreed && agreedData && agreedConsequences && scrolledToBottom;

  useEffect(() => {
    if (!isLastStep) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#F5C842';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches?.[0] || e;
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    };
    const start = (e) => { e.preventDefault(); drawingRef.current = true; const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const draw = (e) => { if (!drawingRef.current) return; e.preventDefault(); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const end = () => { drawingRef.current = false; setSignatureData(canvas.toDataURL('image/png')); };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', draw, { passive: false });
    canvas.addEventListener('touchend', end);
    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', end);
    };
  }, [isLastStep]);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };

  const handleIdUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return;
    setIdPhoto(file);
    setIdPhotoPreview(URL.createObjectURL(file));
  };

  const handleLegalScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 20) {
      setScrolledToBottom(true);
    }
  };

  const handleSign = async () => {
    if (!allFieldsComplete) return;
    setSigning(true);
    try {
      let idPhotoUrl = '';
      if (idPhoto) {
        const fd = new FormData();
        fd.append('file', idPhoto);
        const uploadRes = await fetch(`${API}/api/upload/image`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
        });
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          idPhotoUrl = uploadData.url || '';
        }
      }

      const res = await fetch(`${API}/api/moderator/request-approval`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: formData.full_name,
          country: formData.country,
          id_number: formData.id_number,
          email: formData.email,
          phone: formData.phone,
          id_photo_url: idPhotoUrl,
          signature_data: signatureData,
          agreed_nda: true,
          agreed_data_processing: agreedData,
          agreed_consequences: agreedConsequences,
          scrolled_full_document: scrolledToBottom,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      localStorage.setItem('pulse_moderator_onboarding_done', 'true');
      if (payload.approved === true) {
        onComplete?.('active');
      } else {
        onComplete?.('pending_approval');
      }
    } catch { /* silent */ }
    setSigning(false);
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0A0A0B] border border-[#27272A] rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        {/* Progress */}
        <div className="flex gap-1 p-4 pb-0">
          {[...STEPS, 'sign'].map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= step ? 'bg-pulse-primary' : 'bg-[#27272A]'}`} />
          ))}
        </div>

        <div className="p-6">
          {!isLastStep ? (
            <>
              {isModerationLead && step === 0 && (
                <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-xs text-amber-100/90 leading-relaxed">
                  {lang === 'es'
                    ? 'Eres el Director de confianza y seguridad: tu criterio guía la comunidad. Este mismo acuerdo lo firman quienes incorpores al equipo; tú quedas registrado sin esperar otra validación.'
                    : 'You are the Trust & Safety Lead: your judgment guides the community. The same agreement is signed by people you bring on; your record is sealed without a second gate.'}
                </div>
              )}
              <div className="w-12 h-12 rounded-xl bg-pulse-primary/10 border border-pulse-primary/30 flex items-center justify-center mb-4">
                <currentStep.icon size={24} className="text-pulse-primary" />
              </div>
              <h2 className="font-heading font-bold text-lg mb-3">
                {lang === 'es' ? currentStep.title_es : currentStep.title_en}
              </h2>
              <p className="text-sm text-pulse-muted leading-relaxed mb-6">
                {lang === 'es' ? currentStep.desc_es : currentStep.desc_en}
              </p>
              <div className="flex justify-between">
                <span className="text-[10px] text-pulse-muted">{step + 1} / {STEPS.length + 1}</span>
                <button
                  onClick={() => setStep(step + 1)}
                  className="flex items-center gap-2 bg-pulse-primary text-black px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-pulse-primary-hover transition-colors"
                >
                  {lang === 'es' ? 'Siguiente' : 'Next'} <ArrowRight size={14} />
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-4">
                <FileText size={24} className="text-green-400" />
              </div>
              <h2 className="font-heading font-bold text-lg mb-2">
                {lang === 'es' ? 'Acuerdo de Moderador' : 'Moderator Agreement'}
              </h2>

              {/* Full legal document — must scroll to bottom */}
              <div ref={legalScrollRef} onScroll={handleLegalScroll} className="bg-[#0F0F12] border border-[#1A1A1F] rounded-xl p-4 mb-3 max-h-[180px] overflow-y-auto text-[11px] text-pulse-muted leading-relaxed space-y-2">
                <p><strong>ACUERDO DE CONFIDENCIALIDAD, MODERACION Y PROTECCION DE DATOS — PULSEMUSIC</strong></p>
                <p><strong>Jurisdiccion:</strong> Este acuerdo se rige por las leyes aplicables en la jurisdiccion del firmante y, subsidiariamente, por el Derecho de la Union Europea. En caso de conflicto, las partes se someten a mediacion y, en su defecto, a los tribunales competentes de la sede de PulseMusic.</p>
                <p><strong>1. CONFIDENCIALIDAD.</strong> El firmante se compromete a mantener confidencialidad absoluta sobre toda informacion a la que acceda en el ejercicio de su rol de moderador, incluyendo pero no limitado a: datos personales de usuarios, contenido reportado, decisiones internas de moderacion, metricas de la plataforma, comunicaciones internas y cualquier informacion no publica. Esta obligacion se extiende a cualquier medio: verbal, escrito, digital o visual.</p>
                <p><strong>2. PROTECCION DE DATOS.</strong> El firmante acepta tratar todos los datos personales de acuerdo con el Reglamento General de Proteccion de Datos (RGPD - UE 2016/679), la California Consumer Privacy Act (CCPA) y cualquier legislacion de proteccion de datos aplicable en su jurisdiccion. No extraera, copiara ni almacenara datos personales fuera del sistema de PulseMusic.</p>
                <p><strong>3. CONDUCTA.</strong> El firmante ejercera su rol de moderacion de forma justa, imparcial, proporcional y siguiendo las directrices de la comunidad PulseMusic. No tomara decisiones basadas en prejuicios personales, ideologicos, religiosos, politicos o de cualquier otro tipo.</p>
                <p><strong>4. PROHIBICIONES.</strong> El firmante NO podra: (a) compartir, copiar, distribuir ni divulgar informacion del panel; (b) acceder a funciones fuera de su ambito de moderador; (c) utilizar informacion obtenida para beneficio personal o de terceros; (d) contactar a usuarios fuera de los canales oficiales de la plataforma; (e) aceptar pagos, regalos o incentivos de usuarios.</p>
                <p><strong>5. REGISTRO Y AUDITORIA.</strong> Todas las acciones del moderador quedan registradas con timestamp, IP, identificador de sesion y detalle de la accion. PulseMusic se reserva el derecho de auditar en cualquier momento.</p>
                <p><strong>6. DURACION.</strong> Este acuerdo entra en vigor tras la aprobacion por parte del equipo directivo de PulseMusic y permanece vigente mientras el firmante mantenga el rol de moderador. La obligacion de confidencialidad perdura 24 meses tras la finalizacion de la colaboracion.</p>
                <p><strong>7. REVOCACION.</strong> PulseMusic puede revocar el acceso de moderador en cualquier momento, sin previo aviso y sin obligacion de justificacion, cesando inmediatamente todos los accesos y obligaciones excepto la confidencialidad.</p>
                <p><strong>8. RESPONSABILIDAD.</strong> En caso de incumplimiento probado de este acuerdo, el firmante sera responsable de los danos y perjuicios causados. PulseMusic se reserva el derecho de emprender acciones legales civiles y/o penales en cualquier jurisdiccion aplicable.</p>
                <p><strong>9. IDENTIFICACION.</strong> El firmante proporcionara: nombre legal completo, numero de documento oficial de identidad, pais de residencia, fotografia del documento de identidad y firma manuscrita digital. Estos datos se conservan como prueba del consentimiento informado.</p>
                <p><strong>10. ACEPTACION.</strong> Al firmar este acuerdo, el firmante declara haber leido, comprendido y aceptado integramente todas las clausulas aqui expuestas, actuando de buena fe y con plena capacidad legal.</p>
                <p className="text-[9px] text-[#3F3F46] mt-4">Version 1.0 — PulseMusic {new Date().getFullYear()}</p>
              </div>
              {!scrolledToBottom && (
                <p className="text-[9px] text-amber-400 mb-2 text-center">{lang === 'es' ? 'Desplazate hasta el final del documento para continuar' : 'Scroll to the bottom of the document to continue'}</p>
              )}

              {/* Personal data */}
              <div className="space-y-2 mb-3">
                <input value={formData.full_name} onChange={e => setFormData(f => ({ ...f, full_name: e.target.value }))}
                  placeholder={lang === 'es' ? 'Nombre completo legal *' : 'Full legal name *'}
                  className="w-full bg-[#0F0F12] border border-[#1A1A1F] rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-cyan-500 focus:outline-none" required />
                <div className="grid grid-cols-2 gap-2">
                  <input value={formData.country} onChange={e => setFormData(f => ({ ...f, country: e.target.value }))}
                    placeholder={lang === 'es' ? 'Pais de residencia *' : 'Country *'}
                    className="w-full bg-[#0F0F12] border border-[#1A1A1F] rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-cyan-500 focus:outline-none" required />
                  <input value={formData.id_number} onChange={e => setFormData(f => ({ ...f, id_number: e.target.value }))}
                    placeholder={lang === 'es' ? 'N° documento *' : 'ID number *'}
                    className="w-full bg-[#0F0F12] border border-[#1A1A1F] rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-cyan-500 focus:outline-none" required />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input value={formData.email} onChange={e => setFormData(f => ({ ...f, email: e.target.value }))} type="email"
                    placeholder={lang === 'es' ? 'Email de contacto *' : 'Contact email *'}
                    className="w-full bg-[#0F0F12] border border-[#1A1A1F] rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-cyan-500 focus:outline-none" required />
                  <input value={formData.phone} onChange={e => setFormData(f => ({ ...f, phone: e.target.value }))}
                    placeholder={lang === 'es' ? 'Telefono (opcional)' : 'Phone (optional)'}
                    className="w-full bg-[#0F0F12] border border-[#1A1A1F] rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-cyan-500 focus:outline-none" />
                </div>
              </div>

              {/* ID photo upload */}
              <div className="mb-3">
                <p className="text-[10px] text-pulse-muted mb-1.5">{lang === 'es' ? 'Foto de documento de identidad (DNI/pasaporte) *' : 'ID document photo (passport/national ID) *'}</p>
                {idPhotoPreview ? (
                  <div className="relative">
                    <img src={idPhotoPreview} alt="ID" className="w-full h-32 object-cover rounded-lg border border-[#27272A]" />
                    <button onClick={() => { setIdPhoto(null); setIdPhotoPreview(null); }} className="absolute top-1 right-1 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center text-white text-xs">✕</button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 w-full h-24 border-2 border-dashed border-[#27272A] rounded-lg cursor-pointer hover:border-pulse-primary/40 transition-colors">
                    <Camera size={18} className="text-pulse-muted" />
                    <span className="text-xs text-pulse-muted">{lang === 'es' ? 'Subir foto de documento' : 'Upload ID photo'}</span>
                    <input type="file" accept="image/*" capture="environment" onChange={handleIdUpload} className="hidden" />
                  </label>
                )}
              </div>

              {/* Handwritten signature */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Firma manuscrita digital *' : 'Digital handwritten signature *'}</p>
                  {signatureData && <button onClick={clearSignature} className="text-[9px] text-red-400 hover:text-red-300">{lang === 'es' ? 'Borrar' : 'Clear'}</button>}
                </div>
                <canvas ref={canvasRef} className="w-full h-20 bg-[#0F0F12] border border-[#1A1A1F] rounded-lg cursor-crosshair touch-none" />
                {!signatureData && <p className="text-[9px] text-pulse-muted mt-1 text-center">{lang === 'es' ? 'Dibuja tu firma con el dedo o el raton' : 'Draw your signature with finger or mouse'}</p>}
              </div>

              {/* Triple checkbox */}
              <div className="space-y-2 mb-4">
                <label className="flex items-start gap-2 text-[11px] text-pulse-muted cursor-pointer">
                  <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5 accent-pulse-primary" />
                  <span>{lang === 'es' ? 'He leido y acepto integramente el Acuerdo de Confidencialidad y Moderacion.' : 'I have read and fully accept the Confidentiality and Moderation Agreement.'}</span>
                </label>
                <label className="flex items-start gap-2 text-[11px] text-pulse-muted cursor-pointer">
                  <input type="checkbox" checked={agreedData} onChange={e => setAgreedData(e.target.checked)} className="mt-0.5 accent-pulse-primary" />
                  <span>{lang === 'es' ? 'Autorizo el tratamiento de mis datos personales y documento de identidad conforme al RGPD para la verificacion de mi identidad.' : 'I authorize the processing of my personal data and ID document under GDPR for identity verification.'}</span>
                </label>
                <label className="flex items-start gap-2 text-[11px] text-pulse-muted cursor-pointer">
                  <input type="checkbox" checked={agreedConsequences} onChange={e => setAgreedConsequences(e.target.checked)} className="mt-0.5 accent-pulse-primary" />
                  <span>{lang === 'es' ? 'Entiendo que el incumplimiento de este acuerdo puede conllevar acciones legales civiles y/o penales.' : 'I understand that breach of this agreement may lead to civil and/or criminal legal action.'}</span>
                </label>
              </div>

              <button
                onClick={handleSign}
                disabled={!allFieldsComplete || signing}
                className="w-full bg-green-500 text-black py-3 rounded-xl font-bold text-sm hover:bg-green-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {signing ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <CheckCircle2 size={16} />}
                {lang === 'es' ? 'Firmar acuerdo' : 'Sign agreement'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
